import React, { useState, useEffect } from 'react';
import { KioskHeader, StepDots, formatPhone, formatPrice, KioskKeypad } from './KioskFlow';
import useIdleTimeout, { IdleWarningModal } from './useIdleTimeout';
import { trackAddToCart, trackCheckIn } from './analytics';
import { setKioskContext, kev } from './kioskAnalytics';
import { filterOfferableAddOns, trimSelectionToGap } from './lib/addon-gap';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// Idle timeout per step (seconds)
const CHECKIN_TIMEOUTS = {
  phone: 10,
  name: 30,
  review_thanks: 10,
  booking: 60,
  addons: 90,
  early: 30,
  late_bump: 30,
  late_warning: 60,
  late_success: 15,
  missed: 60,
  no_appointment: 30,
  success: 15, // auto-reset (no warning)
};

// ============================================================
// CHECK-IN FLOW
// Phone → Booking Card → Smart Add-Ons → Success
// ============================================================
export default function KioskCheckIn({ onBack, onBook, onBookFull, centerId, employee, terminal }) {
  const [step, setStep] = useState('phone');
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);
  const [error, setError] = useState(null);

  // Data from Zenoti lookup
  const [guest, setGuest] = useState(null);
  const [multipleGuests, setMultipleGuests] = useState(false);
  const [nameInput, setNameInput] = useState('');
  const [appointment, setAppointment] = useState(null);
  const [gapMinutes, setGapMinutes] = useState(0);
  const [availableAddOns, setAvailableAddOns] = useState([]);

  // Check-in selections
  const [selectedAddOns, setSelectedAddOns] = useState([]);

  // Upsell services (full services at check-in)
  const [upsellCategories, setUpsellCategories] = useState([]);
  const [activeUpsellCat, setActiveUpsellCat] = useState(null);
  const [checkingServiceId, setCheckingServiceId] = useState(null);
  const [serviceAvailability, setServiceAvailability] = useState(null); // { available, sameProvider, soonestSlot, ... }
  const [bookingUpsell, setBookingUpsell] = useState(false);
  const [bookedUpsells, setBookedUpsells] = useState([]); // [{ name, therapistName }]

  // No-appointment state
  const [notFoundType, setNotFoundType] = useState(null); // 'no_appt' or 'no_guest'
  const [upcomingAppointments, setUpcomingAppointments] = useState([]);

  // Google review thank-you (one-time)
  const [showReviewThanks, setShowReviewThanks] = useState(false);

  // Forms SMS
  const [formsSent, setFormsSent] = useState(false);
  const [visitCount, setVisitCount] = useState(null);

  // Late/early arrival state
  const [diffMinutes, setDiffMinutes] = useState(0); // positive = late, negative = early
  const [rescheduleLoading, setRescheduleLoading] = useState(false);
  const [rescheduleSlots, setRescheduleSlots] = useState([]); // available slots for late rescheduling
  const [selectedRescheduleSlot, setSelectedRescheduleSlot] = useState(null);
  const [rebookedInfo, setRebookedInfo] = useState(null); // { time, providerName, isNow }
  const [earlyNowSlot, setEarlyNowSlot] = useState(null); // available "now" slot for early arrivals
  const [earlyRebooking, setEarlyRebooking] = useState(false);

  // Idle timeout — resets to home after inactivity
  // On late_warning, auto-restore the original appointment before going home
  const handleIdleReset = async () => {
    kev(step, 'idle_timeout', { screen: step });
    // No need to restore appointment on late_warning — we no longer cancel upfront
    onBack();
  };

  // Phone + success screens auto-reset silently (no warning needed)
  // Pause idle timer while pageLoading (API in progress — user is waiting, not idle)
  const { showWarning, countdown, dismissWarning } = useIdleTimeout({
    timeoutSeconds: pageLoading ? 0 : (CHECKIN_TIMEOUTS[step] || 0),
    onReset: handleIdleReset,
    autoReset: step === 'phone' || step === 'name' || step === 'success' || step === 'late_success' || step === 'already_checked_in',
  });

  // Demo mode — bypass phone + name screens by mock-injecting guest + appointment.
  //   ?demo=1  (or =true, =checkin)  → land on the "you're early" screen (step='early')
  //                                    with the Check In button ready to tap.
  // Real flow is untouched: the multipleMatches "What's your first name?" disambiguation
  // (line 764+) still runs against the real backend for real guests. Demo never hits
  // the backend so it can't reach that screen.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const demoParam = params.get('demo');
    if (demoParam === '1' || demoParam === 'true' || demoParam === 'checkin') {
      const now = new Date();
      const apptStart = new Date(now.getTime() + 10 * 60 * 1000); // 10 min in the future
      const apptEnd = new Date(apptStart.getTime() + 35 * 60 * 1000);
      const apptTimeStr = apptStart.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
      setGuest({ guestId: 'demo', firstName: 'Demo', lastName: 'Guest', phone: '0000000000', email: 'demo@example.com' });
      setAppointment({
        id: 'demo-appt',
        appointmentGroupId: 'demo-appt',
        time: apptTimeStr,
        startTime: apptStart.toISOString(),
        endTime: apptEnd.toISOString(),
        provider: { id: 'demo-provider', name: 'Vlada', image: null },
        services: [
          { id: 'svc1', name: 'Brazilian', price: 65, duration: 25, provider: 'Vlada' },
          { id: 'svc2', name: 'Underarms', price: 20, duration: 10, provider: 'Vlada' },
        ]
      });
      setDiffMinutes(-10); // 10 min early → step='early' shows the early-arrival banner
      setStep('early');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ============================================================
  // API: Look up guest + today's appointment
  // ============================================================
  async function lookupGuest(overrideFirstName) {
    setLoading(true);
    setPageLoading(true);
    setError(null);
    try {
      const url = overrideFirstName
        ? `${API_URL}/api/kiosk/guest-lookup?phone=${phone}&centerId=${centerId}&firstName=${encodeURIComponent(overrideFirstName)}`
        : `${API_URL}/api/kiosk/guest-lookup?phone=${phone}&centerId=${centerId}&checkTodayAppointments=true`;
      const guestRes = await fetch(url);
      const guestData = await guestRes.json();

      if (guestData.multipleMatches && !overrideFirstName) {
        kev('phone', 'guest_multiple_matches');
        setMultipleGuests(true);
        setStep('name');
        return;
      }

      if (!guestData.found) {
        kev('phone', 'guest_not_found');
        setNotFoundType('no_guest');
        setUpcomingAppointments([]);
        setStep('no_appointment');
        return;
      }
      setKioskContext({ guestId: guestData.guest?.guestId });
      kev('phone', 'guest_found');
      await loadAppointmentForGuest(guestData.guest);
    } catch (err) {
      setError(err.message || 'Could not find your appointment. Please ask the front desk for help.');
    } finally {
      setLoading(false);
      setPageLoading(false);
    }
  }

  // Load appointment data for a resolved guest
  async function loadAppointmentForGuest(resolvedGuest) {
    setGuest(resolvedGuest);

    // 1. Get today's appointments
    const apptRes = await fetch(`${API_URL}/api/kiosk/today-appointments/${resolvedGuest.guestId}?centerId=${centerId}`);
    const apptData = await apptRes.json();
    if (!apptData.appointments || apptData.appointments.length === 0) {
      // Fetch future/other-location appointments to show the guest
      try {
        const futureRes = await fetch(`${API_URL}/api/account/appointments/${resolvedGuest.guestId}`);
        const futureData = await futureRes.json();
        setUpcomingAppointments(futureData.upcoming || []);
      } catch (e) {
        setUpcomingAppointments([]);
      }
      kev('review', 'no_appointment');
      setNotFoundType('no_appt');
      setStep('no_appointment');
      return;
    }
    // Find the first appointment that hasn't been checked in yet.
    // `status` here is OUR normalized enum (2026-07-11 status-enum fix —
    // /api/kiosk/today-appointments now normalizes both its DB-first and
    // live-Zenoti-fallback branches to the same values):
    // 0=booked, 1=confirmed, 2=completed, 3=checked-in, 4=cancelled, 5=no-show.
    // "Already handled, don't check in again" = completed OR checked-in.
    const appt = apptData.appointments.find(a => a.status !== 2 && a.status !== 3)
      || apptData.appointments[0];
    setAppointment(appt);

    // 1b. All appointments already checked in (or done)?
    if (appt.status === 2 || appt.status === 3) {
      setStep('already_checked_in');
      return;
    }

    // 1c. Calculate timing — how early/late is the guest?
    const now = new Date();
    const apptStart = new Date(appt.startTime);
    const diff = (now - apptStart) / 60000; // positive = late, negative = early
    setDiffMinutes(diff);

    // 2. Get therapist gap time
    let gap = 0;
    if (appt.provider) {
      const gapRes = await fetch(`${API_URL}/api/kiosk/therapist-gap?therapistId=${appt.provider.id}&appointmentEndTime=${appt.endTime}&centerId=${centerId}`);
      const gapData = await gapRes.json();
      gap = gapData.gapMinutes || 0;
      setGapMinutes(gap);

      // 3. If there's a gap, load add-on services that fit
      if (gap > 0) {
        const primaryServiceId = appt.services?.[0]?.id;
        if (primaryServiceId) {
          const addOnRes = await fetch(`${API_URL}/api/kiosk/centers/${centerId}/services/${primaryServiceId}/add-ons`);
          const addOnData = await addOnRes.json();
          const optional = addOnData.optional || [];
          // filterOfferableAddOns also drops services already on the appointment —
          // don't offer a Brazilian add-on to someone booked for a Brazilian.
          setAvailableAddOns(filterOfferableAddOns(optional, appt.services, gap));
        }
      }
    }

    // 3b. Upsell services DISABLED for launch — creates standalone bookings that break checkout
    // TODO: Re-enable once add-to-group API is working
    // try {
    //   const excludeIds = (appt.services || []).map(s => s.id).join(',');
    //   const upsellRes = await fetch(`${API_URL}/api/kiosk/centers/${centerId}/upsell-services?maxDuration=45&excludeServiceIds=${excludeIds}`);
    //   const upsellData = await upsellRes.json();
    //   const cats = upsellData.categories || [];
    //   setUpsellCategories(cats);
    //   if (cats.length > 0) setActiveUpsellCat(cats[0].id);
    // } catch (e) {
    //   console.log('Upsell services load failed (non-blocking):', e);
    // }

    // 4. Determine which screen to show based on timing
    const apptEnd = new Date(appt.endTime);
    const pastEndTime = now > apptEnd;

    // Track arrival timing
    const arrivalStatus = pastEndTime ? 'missed' : diff > 5 ? 'late' : diff < -10 ? 'early' : 'on_time';
    kev('review', 'arrival_status', { status: arrivalStatus, minutesDiff: Math.round(diff), services: appt.services?.map(s => s.name) });
    if (gap > 0) kev('review', 'addon_offered', { gapMinutes: gap });

    if (pastEndTime) {
      // FULLY MISSED — past the end time, therapist has moved on
      // Do NOT cancel (stays as no-show for Zenoti to auto-charge)
      setStep('late_bump');
      try {
        // Fetch rebook slots (past appointment doesn't block future slots)
        const slotsRes = await fetch(`${API_URL}/api/kiosk/reschedule-slots`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            centerId,
            services: appt.services.map(s => ({
              serviceId: s.id,
              serviceName: s.name,
              duration: s.duration
            })),
            therapistId: appt.provider?.id,
            isSpecificRequest: false
          })
        });
        const slotsData = await slotsRes.json();
        const nowMs = Date.now();
        const slots = (slotsData.slots || []).map(s => ({
          ...s,
          timeValue: new Date(s.time.replace(' ', 'T')).getTime()
        })).filter(s => s.timeValue >= nowMs - 5 * 60 * 1000);

        setRescheduleSlots(slots);
        setStep('missed');
      } catch (err) {
        console.error('Failed to fetch reschedule slots:', err);
        setStep('missed');
      }
    } else if (diff > 5) {
      // LATE but still within appointment window — show warning, offer check-in or reschedule
      // DO NOT cancel the appointment here — only cancel if they explicitly pick a new time
      // Calculate available slots from provider's schedule (no Catalog API call needed)
      setStep('late_bump');
      try {
        const totalDuration = (appt.services || []).reduce((sum, s) => sum + (s.duration || 0), 0);
        const slotsRes = await fetch(`${API_URL}/api/kiosk/late-reschedule-slots`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            centerId,
            therapistId: appt.provider?.id,
            appointmentStartTime: appt.startTime,
            appointmentEndTime: appt.endTime,
            serviceDuration: totalDuration
          })
        });
        const slotsData = await slotsRes.json();
        const nowMs = Date.now();
        // Filter out past times (browser is in Pacific, server is UTC)
        // and add provider name from appointment (backend doesn't have it)
        const slots = (slotsData.slots || [])
          .map(s => ({
            ...s,
            timeValue: new Date(s.time.replace(' ', 'T')).getTime(),
            therapistName: appt.provider?.name || 'Your provider'
          }))
          .filter(s => s.timeValue >= nowMs - 2 * 60 * 1000); // allow 2 min grace

        setRescheduleSlots(slots);
        setStep('late_warning');
      } catch (err) {
        console.error('Failed to fetch late reschedule slots:', err);
        setStep('late_warning');
      }
    } else if (diff < -10) {
      // More than 10 min early — show early screen, check if provider is free now in background
      setStep('early');
      setEarlyNowSlot(null);
      try {
        const slotsRes = await fetch(`${API_URL}/api/kiosk/reschedule-slots`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            centerId,
            services: appt.services.map(s => ({
              serviceId: s.id,
              serviceName: s.name,
              duration: s.duration
            })),
            therapistId: appt.provider?.id,
            isSpecificRequest: true
          })
        });
        const slotsData = await slotsRes.json();
        const nowMs = Date.now();
        const nowSlot = (slotsData.slots || []).find(s => {
          const slotMs = new Date(s.time.replace(' ', 'T')).getTime();
          return slotMs >= nowMs - 5 * 60 * 1000 && slotMs <= nowMs + 10 * 60 * 1000
            && s.therapistId === appt.provider?.id;
        });
        if (nowSlot) {
          setEarlyNowSlot(nowSlot);
          console.log('[KIOSK] Early arrival: provider is free now, offering Start Now');
        } else {
          console.log('[KIOSK] Early arrival: provider is busy, no Start Now option');
        }
      } catch (e) {
        console.log('[KIOSK] Could not check early availability:', e);
      }
    } else {
      // On time (within -10 to +5 min window)
      // Check if guest left a Google review and hasn't been thanked yet
      try {
        const reviewRes = await fetch(`${API_URL}/api/kiosk/guest-review-status/${resolvedGuest.guestId}`);
        const reviewData = await reviewRes.json();
        if (reviewData.hasReviewed && !reviewData.hasThanked) {
          setShowReviewThanks(true);
          // Mark as thanked immediately
          fetch(`${API_URL}/api/kiosk/mark-review-thanked/${resolvedGuest.guestId}`, { method: 'POST' })
            .catch(err => console.log('Mark thanked error (non-blocking):', err));
          setStep('review_thanks');
          return;
        }
      } catch (e) { console.log('Review status check error (non-blocking):', e); }
      setStep('booking');
    }
  }

  // Rebook at a specific time — cancels old appointment first if needed, then books new one
  async function handleRebook(slotTime, therapistId, { cancelFirst = false } = {}) {
    setRescheduleLoading(true);
    setError(null);
    try {
      // Cancel old appointment first if needed (for late reschedule — appointment still exists)
      if (cancelFirst && appointment?.invoiceId) {
        try {
          await fetch(`${API_URL}/api/kiosk/cancel-appointment`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ invoiceId: appointment.invoiceId })
          });
          await new Promise(r => setTimeout(r, 3000)); // Wait for Zenoti to update
        } catch (e) { console.log('Cancel before rebook failed:', e); }
      }

      const res = await fetch(`${API_URL}/api/kiosk/rebook`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          centerId,
          guestId: guest.guestId,
          services: appointment.services.map(s => ({
            serviceId: s.id,
            therapistId: therapistId || null
          })),
          slotTime,
          notes: 'Rescheduled: late arrival'
        })
      });
      const data = await res.json();
      if (data.success) {
        const newGroupId = data.appointmentGroupId;
        const newApptId = data.appointmentId || newGroupId;
        if (newGroupId) {
          setAppointment(prev => ({ ...prev, id: newApptId, appointmentId: newApptId, appointmentGroupId: newGroupId }));

          // Figure out the rebooked time and if it's right now or later
          const rebookedTime = new Date(slotTime.replace(' ', 'T'));
          const minsFromNow = (rebookedTime - Date.now()) / 60000;
          // Use the actual therapist name from Zenoti's confirm response first,
          // then fall back to the slot info or original provider
          const providerName = data.therapistName
            || rescheduleSlots.find(s => s.therapistId === therapistId)?.therapistName
            || appointment.provider?.name
            || 'Your provider';

          setRebookedInfo({
            time: rebookedTime.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }),
            providerName,
            isNow: minsFromNow <= 10
          });

          trackCheckIn(guest?.firstName, appointment.services, providerName);
          kev('late_warning', 'reschedule_success', { newTime: slotTime, provider: providerName });
          setStep('late_success');

          // Check in the new appointment in the background with retry
          // Zenoti needs time to finalize the booking before check-in works
          const doCheckIn = async (attempt = 1) => {
            try {
              // Try individual check-in first (more reliable for freshly booked appointments)
              const ciRes = await fetch(`${API_URL}/api/kiosk/checkin`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ appointmentId: newApptId, appointmentGroupId: newGroupId, centerId, guestId: guest?.guestId })
              });
              const ciData = await ciRes.json();
              if (!ciRes.ok && attempt < 5) {
                console.log(`Check-in attempt ${attempt} failed (${ciRes.status}), retrying in ${attempt < 3 ? 5 : 8}s...`);
                setTimeout(() => doCheckIn(attempt + 1), attempt < 3 ? 5000 : 8000);
              } else {
                console.log(`Check-in after rebook: attempt=${attempt}, ok=${ciRes.ok}`, ciData);
              }
            } catch (e) {
              if (attempt < 5) {
                console.log(`Check-in attempt ${attempt} error, retrying...`, e);
                setTimeout(() => doCheckIn(attempt + 1), attempt < 3 ? 5000 : 8000);
              } else {
                console.log('Check-in after rebook failed after 5 attempts:', e);
              }
            }
          };
          // Wait 5 seconds before first attempt — Zenoti needs time to finalize
          setTimeout(() => doCheckIn(), 5000);
        } else {
          confirmCheckIn();
        }
      } else {
        console.log('Rebook failed:', data.error);
        setError(data.error || 'Could not book that time — please ask the front desk.');
      }
    } catch (err) {
      console.error('Reschedule error:', err);
      setError('Could not reschedule — please ask the front desk.');
    } finally {
      setRescheduleLoading(false);
    }
  }

  // Early arrival: cancel original, rebook to now, with safety net
  async function handleEarlyRebook() {
    if (!earlyNowSlot || !appointment) return;
    setEarlyRebooking(true);
    setError(null);
    const originalTime = appointment.startTime;
    const originalInvoiceId = appointment.invoiceId;
    try {
      // Step 1: Cancel original appointment
      if (originalInvoiceId) {
        await fetch(`${API_URL}/api/kiosk/cancel-appointment`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ invoiceId: originalInvoiceId })
        });
      }
      // Step 2: Wait for Zenoti to sync
      await new Promise(r => setTimeout(r, 3000));
      // Step 3: Rebook at "now" with same provider
      const res = await fetch(`${API_URL}/api/kiosk/rebook`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          centerId,
          guestId: guest.guestId,
          services: appointment.services.map(s => ({
            serviceId: s.id,
            therapistId: appointment.provider?.id || null
          })),
          slotTime: earlyNowSlot.time,
          notes: 'Rescheduled: early arrival'
        })
      });
      const data = await res.json();
      if (data.success && data.appointmentGroupId) {
        const newGroupId = data.appointmentGroupId;
        const newApptId = data.appointmentId || newGroupId;
        setAppointment(prev => ({ ...prev, id: newApptId, appointmentId: newApptId, appointmentGroupId: newGroupId }));
        setRebookedInfo({
          time: new Date(earlyNowSlot.time.replace(' ', 'T')).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }),
          providerName: appointment.provider?.name || 'Your provider',
          isNow: true
        });
        trackCheckIn(guest?.firstName, appointment.services, appointment.provider?.name);
        setStep('late_success');
        // Check in the new appointment in background with retry
        const doCheckIn = async (attempt = 1) => {
          try {
            const ciRes = await fetch(`${API_URL}/api/kiosk/checkin`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ appointmentId: newApptId, appointmentGroupId: newGroupId, centerId, guestId: guest?.guestId })
            });
            if (!ciRes.ok && attempt < 3) {
              console.log(`Early rebook check-in attempt ${attempt} failed, retrying...`);
              setTimeout(() => doCheckIn(attempt + 1), 5000);
            }
          } catch (e) {
            if (attempt < 3) setTimeout(() => doCheckIn(attempt + 1), 3000);
            else console.log('Check-in after early rebook failed after 3 attempts:', e);
          }
        };
        setTimeout(() => doCheckIn(), 3000);
      } else {
        // Rebook at "now" failed — try to restore original appointment
        console.log('[KIOSK] Early rebook failed, restoring original:', data.error);
        try {
          await fetch(`${API_URL}/api/kiosk/rebook`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              centerId,
              guestId: guest.guestId,
              services: appointment.services.map(s => ({
                serviceId: s.id,
                therapistId: appointment.provider?.id || null
              })),
              slotTime: originalTime,
              notes: 'Restored: early rebook failed'
            })
          });
          setError('Could not move to now — your original appointment has been kept.');
        } catch (restoreErr) {
          console.error('[KIOSK] Could not restore original appointment:', restoreErr);
          setError('Something went wrong — please ask the front desk.');
        }
      }
    } catch (err) {
      console.error('[KIOSK] Early rebook error:', err);
      setError('Could not reschedule — please ask the front desk.');
    } finally {
      setEarlyRebooking(false);
    }
  }

  // ============================================================
  // ADD-ON HELPERS
  // ============================================================
  // The gap moves during the day (receptionists shuffle lunches/blockouts), so the
  // load-time value can go stale while the guest is on this screen. Re-check on
  // add-on tap and again right before check-in; prune selections that no longer fit.
  async function refreshGap() {
    if (!appointment?.provider) return gapMinutes;
    try {
      const res = await fetch(`${API_URL}/api/kiosk/therapist-gap?therapistId=${appointment.provider.id}&appointmentEndTime=${appointment.endTime}&centerId=${centerId}`);
      const data = await res.json();
      const fresh = data.gapMinutes || 0;
      setGapMinutes(fresh);
      setSelectedAddOns(prev => {
        const kept = trimSelectionToGap(prev, availableAddOns, fresh);
        if (kept.length !== prev.length) {
          kev('addons', 'addon_dropped_stale_gap', { before: prev.length, after: kept.length, freshGap: fresh });
        }
        return kept;
      });
      return fresh;
    } catch (e) {
      return gapMinutes; // non-blocking — confirm-time check is the backstop
    }
  }

  function toggleAddOn(id) {
    setSelectedAddOns(prev => {
      if (prev.includes(id)) {
        kev('addons', 'addon_removed', { addonId: id });
        return prev.filter(x => x !== id);
      }
      const addon = availableAddOns.find(a => a.id === id);
      if (addon) {
        trackAddToCart(addon.name, addon.price, 'Check-In Add-On');
        kev('addons', 'addon_accepted', { addonId: id, addonName: addon.name, addonPrice: addon.price });
      }
      return [...prev, id];
    });
    refreshGap(); // fire-and-forget — state updates re-gate the cards
  }

  function getRemainingGap() {
    const used = selectedAddOns.reduce((sum, id) => {
      const a = availableAddOns.find(x => x.id === id);
      return sum + (a ? a.duration : 0);
    }, 0);
    return gapMinutes - used;
  }

  function getSelectedAddOnPrice() {
    return selectedAddOns.reduce((sum, id) => {
      const a = availableAddOns.find(x => x.id === id);
      return sum + (a ? a.price : 0);
    }, 0);
  }

  // ============================================================
  // UPSELL SERVICE HELPERS
  // ============================================================
  async function checkServiceAvailability(service) {
    setCheckingServiceId(service.id);
    setServiceAvailability(null);
    try {
      const res = await fetch(`${API_URL}/api/kiosk/check-service-availability`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          centerId,
          serviceId: service.id,
          serviceName: service.name,
          duration: service.duration,
          therapistId: appointment?.provider?.id,
          guestId: guest?.guestId,
          appointmentEndTime: appointment?.endTime
        })
      });
      const data = await res.json();
      setServiceAvailability({ ...data, service });
    } catch (e) {
      setServiceAvailability({ available: false, service, error: true });
    } finally {
      setCheckingServiceId(null);
    }
  }

  async function bookUpsellService(service, therapistId, slotTime) {
    setBookingUpsell(true);
    try {
      const res = await fetch(`${API_URL}/api/kiosk/book-additional-service`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          centerId,
          guestId: guest?.guestId,
          serviceId: service.id,
          therapistId,
          slotTime,
          appointmentGroupId: appointment?.appointmentGroupId,
          appointmentId: appointment?.id,
          notes: 'Added at kiosk check-in'
        })
      });
      const data = await res.json();
      if (data.success) {
        const therapistName = data.therapistName || 'your provider';
        setBookedUpsells(prev => [...prev, { name: service.name, therapistName }]);
        // Show booked confirmation in the modal instead of closing it
        const slotDate = new Date(slotTime.replace(' ', 'T'));
        const timeStr = slotDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
        setServiceAvailability(prev => ({
          ...prev,
          booked: true,
          bookedTime: timeStr,
          bookedTherapist: therapistName
        }));
        trackAddToCart(service.name, service.price, 'Check-In Upsell Service');
      } else {
        setServiceAvailability(prev => ({ ...prev, error: true, errorMsg: data.error || 'Booking failed' }));
      }
    } catch (e) {
      setServiceAvailability(prev => ({ ...prev, error: true, errorMsg: 'Something went wrong' }));
    } finally {
      setBookingUpsell(false);
    }
  }

  // ============================================================
  // CHECK-IN API CALLS
  // ============================================================
  async function confirmCheckIn() {
    if (!appointment) { setStep('success'); return; }

    // Final gap re-check: the therapist's schedule may have changed since the
    // add-ons were offered. Never book an add-on into a lunch that appeared.
    let finalSelection = selectedAddOns;
    if (selectedAddOns.length > 0) {
      const fresh = await refreshGap();
      finalSelection = trimSelectionToGap(selectedAddOns, availableAddOns, fresh);
    }
    const addOnServiceIds = finalSelection
      .map(id => availableAddOns.find(a => a.id === id)?.id)
      .filter(Boolean);

    try {
      await fetch(`${API_URL}/api/kiosk/checkin`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          appointmentId: appointment.id,
          appointmentGroupId: appointment.appointmentGroupId,
          addOnServiceIds,
          centerId
        })
      });
    } catch (err) {
      console.error('Check-in API error:', err);
    }
    trackCheckIn(guest?.firstName, appointment.services, appointment.provider?.name);
    kev('success', 'checkin_success', {
      services: appointment.services?.map(s => s.name),
      addonsSelected: finalSelection.length,
      provider: appointment.provider?.name,
    });

    // Check for unfilled forms and text the guest a link (non-blocking)
    // Guest forms only sent to first-time guests (returning clients filled paper forms)
    // Service forms sent to everyone
    if (appointment.appointmentGroupId && guest?.guestId && guest?.phone) {
      (async () => {
        try {
          const [formsRes, historyRes] = await Promise.all([
            fetch(`${API_URL}/api/booking/forms?appointmentGroupId=${appointment.appointmentGroupId}&guestId=${guest.guestId}`),
            fetch(`${API_URL}/api/kiosk/guest-history/${guest.guestId}?centerId=${centerId}`)
          ]);
          const formsData = await formsRes.json();
          const historyData = await historyRes.json();
          const isFirstVisit = historyData.isFirstVisit;
          if (historyData.totalVisits > 0) setVisitCount(historyData.totalVisits);

          console.log(`[KIOSK] Forms check: ${guest.firstName}, firstVisit=${isFirstVisit}, allForms=${(formsData.forms || []).length}, hasUnfilled=${formsData.hasUnfilledForms}`);
          (formsData.forms || []).forEach(f => console.log(`[KIOSK]   Form: "${f.name}" type=${f.formType}`));

          // Filter: service forms always, guest forms only for first-timers
          const relevantForms = (formsData.forms || []).filter(f =>
            f.formType === 'service' || (f.formType === 'guest' && isFirstVisit)
          );

          console.log(`[KIOSK] Relevant forms after filter: ${relevantForms.length}`);

          if (relevantForms.length > 0) {
            const smsRes = await fetch(`${API_URL}/api/kiosk/send-forms-link`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ phone: guest.phone, guestName: guest.firstName })
            });
            const smsData = await smsRes.json();
            console.log(`[KIOSK] Forms SMS result:`, smsData);
            if (smsData.success) setFormsSent(true);
          }
        } catch (err) {
          console.error('Forms check/send error (non-blocking):', err);
        }
      })();
    } else {
      console.log(`[KIOSK] Forms check skipped: groupId=${appointment?.appointmentGroupId}, guestId=${guest?.guestId}, phone=${guest?.phone}`);
      // Still fetch visit count for the success screen
      if (guest?.guestId) {
        fetch(`${API_URL}/api/kiosk/guest-history/${guest.guestId}?centerId=${centerId}`)
          .then(r => r.json())
          .then(d => { if (d.totalVisits > 0) setVisitCount(d.totalVisits); })
          .catch(() => {});
      }
    }

    kev('success', 'flow_complete');
    setStep('success');
  }

  // ============================================================
  // GROUP CHECK-IN
  // ============================================================
  // ============================================================
  // RENDER
  // ============================================================
  return (
    <div className="kiosk-container">
      <KioskHeader />

      {/* Full-screen loading overlay for page transitions */}
      {pageLoading && (
        <div className="kiosk-page-loading-overlay">
          <div className="kiosk-page-spinner" />
        </div>
      )}

      <div className="kiosk-main">

        {/* PHONE INPUT */}
        {step === 'phone' && (
          <div className="kiosk-screen kiosk-phone-screen kiosk-fade-in" style={{ flex: 'none' }}>
            <div className="kiosk-screen-title">Enter your phone number</div>
            <div className="kiosk-screen-sub">We'll pull up your appointment</div>
            <input
              className="kiosk-phone-input"
              type="tel"
              placeholder="(604) 555-1234"
              value={formatPhone(phone)}
              readOnly
            />
            <KioskKeypad
              mode="numeric"
              value={phone}
              onChange={v => setPhone(v.slice(0, 10))}
              maxLength={10}
            />
            {error && <div style={{ color: '#c44', fontSize: 13, textAlign: 'center' }}>{error}</div>}
            <button className="kiosk-btn-primary" disabled={phone.length < 10 || loading} onClick={() => lookupGuest()}>
              {loading ? 'Looking up...' : 'Find Me'}
            </button>
            <button className="kiosk-nav-back" onClick={onBack}>← Back</button>
          </div>
        )}

        {/* FIRST NAME — multiple accounts on this phone */}
        {step === 'name' && (
          <div className="kiosk-screen kiosk-phone-screen kiosk-fade-in">
            <div className="kiosk-screen-title">What's your first name?</div>
            <div className="kiosk-screen-sub">We found more than one account with this number</div>
            <input
              className="kiosk-phone-input"
              type="text"
              placeholder="First name"
              value={nameInput}
              readOnly
            />
            <KioskKeypad
              mode="alpha"
              value={nameInput}
              onChange={setNameInput}
              maxLength={30}
            />
            {error && <div style={{ color: '#c44', fontSize: 13, textAlign: 'center' }}>{error}</div>}
            <button
              className="kiosk-btn-primary"
              disabled={!nameInput.trim() || loading}
              onClick={() => lookupGuest(nameInput.trim())}
            >
              {loading ? 'Looking up...' : 'Continue'}
            </button>
            <button className="kiosk-nav-back" onClick={() => { setError(null); setNameInput(''); setStep('phone'); }}>← Back</button>
          </div>
        )}

        {/* GOOGLE REVIEW THANK YOU — one-time at check-in */}
        {step === 'review_thanks' && (
          <div className="kiosk-screen kiosk-checkin-screen kiosk-fade-in" style={{ justifyContent: 'center', textAlign: 'center' }}>
            <div style={{ fontSize: 44, marginBottom: 8 }}>⭐</div>
            <div className="kiosk-screen-title" style={{ fontSize: 22, marginBottom: 4 }}>
              Thank you for your Google review, {guest?.firstName}!
            </div>
            <div className="kiosk-screen-sub" style={{ marginBottom: 32, maxWidth: 360 }}>
              It means so much to {appointment?.provider?.name || 'our team'}. We really appreciate you!
            </div>
            <div className="kiosk-bottom-bar">
              <button className="kiosk-nav-forward" style={{ flex: 1, maxWidth: 280 }} onClick={() => setStep('booking')}>
                Continue to Check In
              </button>
            </div>
          </div>
        )}

        {/* BOOKING CARD */}
        {step === 'booking' && appointment && (
          <div className="kiosk-screen kiosk-checkin-screen kiosk-fade-in">
            <div className="kiosk-greeting">Hi {guest.firstName}!</div>

            <>
              <div className="kiosk-booking-card kiosk-slide-up kiosk-delay-1">
                <div className="kiosk-booking-card-top">
                  <div className="kiosk-booking-card-name">Today at {appointment.time}</div>
                </div>
                <div className="kiosk-booking-card-services">
                  {appointment.services.map(s => s.name).join(' + ')}
                </div>
                <div className="kiosk-booking-card-provider">
                  with {appointment.provider.name} · {appointment.services.reduce((s, x) => s + x.duration, 0)} min
                </div>
              </div>
              <button
                className="kiosk-btn-primary"
                onClick={() => {
                  if ((gapMinutes <= 0 || availableAddOns.length === 0) && upsellCategories.length === 0) {
                    confirmCheckIn();
                  } else {
                    setStep('addons');
                  }
                }}
              >
                Check In
              </button>
              <button className="kiosk-nav-back" onClick={onBack}>Cancel</button>
            </>
          </div>
        )}

        {/* ADD-ONS + UPSELL SERVICES */}
        {step === 'addons' && (
          <div className="kiosk-screen kiosk-checkin-screen kiosk-fade-in">
            <div className="kiosk-screen-title">Add to your visit?</div>
            {gapMinutes <= 0 && availableAddOns.length === 0 && upsellCategories.length > 0 && (
              <div className="kiosk-screen-sub">Browse available services with any provider</div>
            )}

            {/* Add-ons section (same provider, fits in gap) */}
            {gapMinutes > 0 && availableAddOns.length > 0 && (
              <>
                <div className="kiosk-screen-sub">
                  {appointment.provider.name} has {gapMinutes} min available after your appointment
                </div>
                <div className="kiosk-slide-up kiosk-delay-1" style={{ width: '100%' }}>
                  {availableAddOns.map(addon => {
                    const isSelected = selectedAddOns.includes(addon.id);
                    const remaining = getRemainingGap();
                    if (!isSelected && addon.duration > remaining) return null;
                    return (
                      <div
                        key={addon.id}
                        className={`kiosk-addon-card ${isSelected ? 'selected' : ''}`}
                        onClick={() => toggleAddOn(addon.id)}
                      >
                        <div className="kiosk-addon-info">
                          <div className="kiosk-addon-name">{addon.name}</div>
                          <div className="kiosk-addon-meta">{addon.duration} min</div>
                        </div>
                        <div className="kiosk-addon-price">{formatPrice(addon.price)}</div>
                        <div className="kiosk-addon-check">{isSelected ? '✓' : ''}</div>
                      </div>
                    );
                  })}
                  {selectedAddOns.length > 0 && (
                    <div className="kiosk-addon-remaining">
                      {getRemainingGap()} min remaining · {formatPrice(getSelectedAddOnPrice())} added
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Upsell services section */}
            {upsellCategories.length > 0 && (
              <div className={`kiosk-upsell-section ${gapMinutes > 0 && availableAddOns.length > 0 ? 'has-addons-above' : ''}`}>
                <div className="kiosk-upsell-title">Want another service?</div>

                {/* Already booked upsells */}
                {bookedUpsells.map((b, i) => (
                  <div key={i} className="kiosk-upsell-booked">
                    <span className="kiosk-upsell-booked-check">✓</span>
                    <span>{b.name} booked with {b.therapistName}</span>
                  </div>
                ))}

                {/* Category tabs */}
                <div className="kiosk-category-tabs">
                  {upsellCategories.map(cat => (
                    <button
                      key={cat.id}
                      className={`kiosk-category-tab ${activeUpsellCat === cat.id ? 'active' : ''}`}
                      onClick={() => setActiveUpsellCat(cat.id)}
                    >
                      {cat.name}
                    </button>
                  ))}
                </div>

                {/* Service list for active category, grouped by subcategory */}
                {activeUpsellCat && (() => {
                  const cat = upsellCategories.find(c => c.id === activeUpsellCat);
                  if (!cat) return null;
                  const bookedIds = new Set(bookedUpsells.map(b => b.id));
                  const services = cat.services.filter(s => !bookedIds.has(s.id));
                  if (services.length === 0) return <div className="kiosk-upsell-empty">All services in this category have been added!</div>;

                  // Group by subcategory
                  const groups = {};
                  for (const svc of services) {
                    const key = svc.subCategory || '';
                    if (!groups[key]) groups[key] = [];
                    groups[key].push(svc);
                  }
                  const groupEntries = Object.entries(groups).sort(([a], [b]) => {
                    if (!a) return 1; if (!b) return -1;
                    return a.localeCompare(b);
                  });

                  return (
                    <div className="kiosk-upsell-services">
                      {groupEntries.map(([subCat, svcs]) => (
                        <div key={subCat || '_none'}>
                          {subCat && <div className="kiosk-upsell-subcat">{subCat}</div>}
                          {svcs.map(svc => {
                            const isChecking = checkingServiceId === svc.id;
                            return (
                              <div
                                key={svc.id}
                                className={`kiosk-upsell-card ${isChecking ? 'checking' : ''}`}
                                onClick={() => !isChecking && !bookingUpsell && checkServiceAvailability(svc)}
                              >
                                <div className="kiosk-upsell-card-main">
                                  <div className="kiosk-upsell-card-name">{svc.name}</div>
                                  {svc.description && <div className="kiosk-upsell-card-desc">{svc.description}</div>}
                                  <div className="kiosk-upsell-card-meta">{svc.duration} min · {formatPrice(svc.price)}</div>
                                </div>
                                {isChecking ? (
                                  <div className="kiosk-upsell-spinner" />
                                ) : (
                                  <div className="kiosk-upsell-card-arrow">›</div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ))}
                    </div>
                  );
                })()}

                {/* Availability result modal */}
                {serviceAvailability && (
                  <div className="kiosk-upsell-result-overlay" onClick={() => !bookingUpsell && !serviceAvailability.booked && setServiceAvailability(null)}>
                    <div className="kiosk-upsell-result-modal" onClick={e => e.stopPropagation()}>
                      <div className="kiosk-upsell-result-service">{serviceAvailability.service?.name}</div>
                      <div className="kiosk-upsell-result-meta">{serviceAvailability.service?.duration} min · {formatPrice(serviceAvailability.service?.price)}</div>

                      {serviceAvailability.booked ? (
                        <>
                          <div className="kiosk-upsell-result-booked-icon">✓</div>
                          <div className="kiosk-upsell-result-msg available" style={{ fontWeight: 700 }}>
                            Booked!
                          </div>
                          <div className="kiosk-upsell-result-gap">
                            {serviceAvailability.bookedTime} with {serviceAvailability.bookedTherapist}
                          </div>
                          {serviceAvailability.soonestSlot?.gapMinutes != null && serviceAvailability.soonestSlot.gapMinutes > 5 && (
                            <div className="kiosk-upsell-result-wait-notice">
                              Your appointment ends first — there's about a {serviceAvailability.soonestSlot.gapMinutes} minute wait before this service starts. Please take a seat and we'll call you!
                            </div>
                          )}
                          <button className="kiosk-upsell-result-btn confirm" onClick={() => setServiceAvailability(null)}>
                            Got it
                          </button>
                        </>
                      ) : serviceAvailability.error ? (
                        <>
                          <div className="kiosk-upsell-result-msg sorry">{serviceAvailability.errorMsg || 'Something went wrong — please ask the front desk.'}</div>
                          <button className="kiosk-upsell-result-btn dismiss" onClick={() => setServiceAvailability(null)}>OK</button>
                        </>
                      ) : !serviceAvailability.available ? (
                        <>
                          <div className="kiosk-upsell-result-msg sorry">Sorry, no one's available for this service right now</div>
                          <button className="kiosk-upsell-result-btn dismiss" onClick={() => setServiceAvailability(null)}>OK</button>
                        </>
                      ) : (() => {
                        // Determine best slot to show — prefer same provider, fall back to other
                        const sameSlot = serviceAvailability.soonestSameProvider;
                        const otherSlot = serviceAvailability.soonestOtherProvider;
                        const bestSlot = serviceAvailability.soonestSlot;
                        const isSame = bestSlot?.isSameProvider;

                        // Format the slot time
                        const formatSlotTime = (time) => {
                          if (!time) return '';
                          const t = new Date(time.replace(' ', 'T'));
                          return t.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
                        };

                        // Format gap
                        const formatGap = (mins) => {
                          if (mins == null) return null;
                          if (mins <= 0) return 'right after your appointment';
                          if (mins < 60) return `${mins} min wait after your appointment`;
                          const hrs = Math.floor(mins / 60);
                          const remaining = mins % 60;
                          return remaining > 0 ? `${hrs}h ${remaining}min wait` : `${hrs}h wait`;
                        };

                        return isSame ? (
                          <>
                            <div className="kiosk-upsell-result-msg available">
                              {appointment.provider.name} can fit this in at {formatSlotTime(bestSlot.time)}
                            </div>
                            {bestSlot.gapMinutes != null && (
                              <div className="kiosk-upsell-result-gap">
                                {bestSlot.gapMinutes <= 5 ? 'Right after your appointment' : formatGap(bestSlot.gapMinutes)}
                              </div>
                            )}
                            <div className="kiosk-upsell-result-actions">
                              <button className="kiosk-upsell-result-btn cancel" onClick={() => setServiceAvailability(null)} disabled={bookingUpsell}>Cancel</button>
                              <button
                                className="kiosk-upsell-result-btn confirm"
                                disabled={bookingUpsell}
                                onClick={() => bookUpsellService(serviceAvailability.service, bestSlot.therapistId, bestSlot.time)}
                              >
                                {bookingUpsell ? 'Booking...' : 'Add to Visit'}
                              </button>
                            </div>
                            {/* If another provider is available sooner, show that option too */}
                            {otherSlot && otherSlot.gapMinutes != null && sameSlot?.gapMinutes != null && otherSlot.gapMinutes < sameSlot.gapMinutes - 10 && (
                              <div className="kiosk-upsell-result-alt">
                                <div className="kiosk-upsell-result-alt-text">
                                  Or {otherSlot.therapistName} at {formatSlotTime(otherSlot.time)} — {formatGap(otherSlot.gapMinutes)}
                                </div>
                                <button
                                  className="kiosk-upsell-result-btn alt"
                                  disabled={bookingUpsell}
                                  onClick={() => bookUpsellService(serviceAvailability.service, otherSlot.therapistId, otherSlot.time)}
                                >
                                  {bookingUpsell ? 'Booking...' : `Book with ${otherSlot.therapistName}`}
                                </button>
                              </div>
                            )}
                          </>
                        ) : (
                          <>
                            <div className="kiosk-upsell-result-msg available">
                              {appointment.provider.name} doesn't have time, but {bestSlot.therapistName} is available at {formatSlotTime(bestSlot.time)}
                            </div>
                            {bestSlot.gapMinutes != null && (
                              <div className="kiosk-upsell-result-gap">
                                {bestSlot.gapMinutes <= 5 ? 'Right after your appointment' : formatGap(bestSlot.gapMinutes)}
                              </div>
                            )}
                            <div className="kiosk-upsell-result-actions">
                              <button className="kiosk-upsell-result-btn cancel" onClick={() => setServiceAvailability(null)} disabled={bookingUpsell}>Cancel</button>
                              <button
                                className="kiosk-upsell-result-btn confirm"
                                disabled={bookingUpsell}
                                onClick={() => bookUpsellService(serviceAvailability.service, bestSlot.therapistId, bestSlot.time)}
                              >
                                {bookingUpsell ? 'Booking...' : `Book with ${bestSlot.therapistName}`}
                              </button>
                            </div>
                          </>
                        );
                      })()}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* No add-ons and no upsell — shouldn't normally reach here but just in case */}
            {gapMinutes <= 0 && availableAddOns.length === 0 && upsellCategories.length === 0 && (
              <div className="kiosk-no-addons-note">
                No add-ons available today. No worries!
              </div>
            )}

            <button
              className="kiosk-btn-primary"
              onClick={() => confirmCheckIn()}
            >
              {(selectedAddOns.length > 0 || bookedUpsells.length > 0)
                ? `Check In + ${selectedAddOns.length + bookedUpsells.length} Added`
                : 'Check In'}
            </button>
            <button
              className="kiosk-nav-back"
              onClick={() => { setSelectedAddOns([]); confirmCheckIn(); }}
            >
              No Thanks
            </button>
          </div>
        )}

        {/* EARLY ARRIVAL (>10 min early) */}
        {step === 'early' && appointment && (
          <div className="kiosk-screen kiosk-checkin-screen kiosk-fade-in" style={{ justifyContent: 'center', textAlign: 'center' }}>
            <div className="kiosk-late-banner" style={{ background: 'linear-gradient(135deg, #00897B, #26A69A)' }}>
              <div className="kiosk-late-icon">🕐</div>
              <div className="kiosk-late-label">{Math.abs(Math.round(diffMinutes))} min Early</div>
            </div>
            <div className="kiosk-greeting">Hi {guest?.firstName}!</div>
            <div className="kiosk-booking-card kiosk-slide-up kiosk-delay-1" style={{ textAlign: 'center' }}>
              {earlyNowSlot ? (
                <>
                  <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>
                    {appointment.provider?.name || 'Your provider'} is available now!
                  </div>
                  <div style={{ fontSize: 15, color: '#666', lineHeight: 1.5 }}>
                    Would you like to start your appointment now instead of waiting until {appointment.time}?
                  </div>
                </>
              ) : (
                <>
                  <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>
                    Your appointment isn't until {appointment.time}
                  </div>
                  <div style={{ fontSize: 15, color: '#666', lineHeight: 1.5 }}>
                    You're a bit early! Please have a seat and {appointment.provider?.name || 'your therapist'} will be with you shortly.
                  </div>
                </>
              )}
            </div>
            {earlyNowSlot ? (
              <>
                <button
                  className="kiosk-btn-primary"
                  disabled={earlyRebooking}
                  onClick={handleEarlyRebook}
                >
                  {earlyRebooking ? 'Moving...' : 'Start Now'}
                </button>
                <button
                  className="kiosk-nav-back"
                  disabled={earlyRebooking}
                  onClick={() => {
                    if ((gapMinutes <= 0 || availableAddOns.length === 0) && upsellCategories.length === 0) {
                      confirmCheckIn();
                    } else {
                      setStep('addons');
                    }
                  }}
                >
                  Check In at {appointment.time}
                </button>
                <button className="kiosk-nav-back" disabled={earlyRebooking} onClick={onBack}>Cancel</button>
              </>
            ) : (
              <>
                <button
                  className="kiosk-btn-primary"
                  onClick={() => {
                    if ((gapMinutes <= 0 || availableAddOns.length === 0) && upsellCategories.length === 0) {
                      confirmCheckIn();
                    } else {
                      setStep('addons');
                    }
                  }}
                >
                  Check In
                </button>
                <button className="kiosk-nav-back" onClick={onBack}>Cancel</button>
              </>
            )}
          </div>
        )}

        {/* LATE ARRIVAL — loading slots */}
        {step === 'late_bump' && (
          <div className="kiosk-screen kiosk-checkin-screen kiosk-fade-in" style={{ justifyContent: 'center', textAlign: 'center' }}>
            <div className="kiosk-late-banner">
              <div className="kiosk-late-icon">⏰</div>
              <div className="kiosk-late-label">{Math.round(diffMinutes)} min Late</div>
            </div>
            <div className="kiosk-greeting">Hi {guest?.firstName}!</div>
            <div style={{ fontSize: 16, color: '#666', marginTop: 8 }}>
              Finding available times for you...
            </div>
            <div className="kiosk-late-spinner" />
          </div>
        )}

        {/* MISSED APPOINTMENT — past end time, therapist has moved on */}
        {step === 'missed' && appointment && (
          <div className="kiosk-screen kiosk-checkin-screen kiosk-fade-in" style={{ textAlign: 'center' }}>
            <div className="kiosk-late-banner" style={{ background: 'linear-gradient(135deg, #D32F2F, #E53935)' }}>
              <div className="kiosk-late-icon">⏰</div>
              <div className="kiosk-late-label">Missed</div>
            </div>
            <div className="kiosk-greeting">Hi {guest?.firstName}!</div>
            {error && (
              <div style={{ background: '#FDEDED', border: '1.5px solid #F5C6CB', borderRadius: 10, padding: '10px 16px', color: '#842029', fontSize: 14, width: '100%', textAlign: 'center', marginBottom: 4 }}>
                {error}
              </div>
            )}
            <div className="kiosk-late-warning-card kiosk-slide-up kiosk-delay-1" style={{ borderColor: '#D32F2F' }}>
              <div className="kiosk-late-warning-title" style={{ color: '#C62828' }}>
                You missed your {appointment.time} appointment
              </div>
              <div className="kiosk-late-warning-body">
                {appointment.provider?.name || 'Your provider'} has moved on to their next guest. A late cancellation fee may apply.
              </div>
            </div>

            {/* Rebook options */}
            {rescheduleSlots.length > 0 ? (
              <div style={{ width: '100%', marginTop: 4 }}>
                <div className="kiosk-late-alert" style={{ marginBottom: 12 }}>
                  Want to rebook for today?
                </div>
                <div style={{ maxHeight: 240, overflowY: 'auto' }}>
                  {rescheduleSlots.map((slot, i) => {
                    const t = new Date(slot.time.replace(' ', 'T'));
                    const displayTime = t.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
                    const isSelected = selectedRescheduleSlot === i;
                    const isSameProvider = slot.therapistId === appointment.provider?.id;
                    return (
                      <div
                        key={i}
                        className={`kiosk-late-slot ${isSelected ? 'selected' : ''}`}
                        onClick={() => setSelectedRescheduleSlot(i)}
                      >
                        <div className="kiosk-late-slot-time">{displayTime}</div>
                        <div className="kiosk-late-slot-provider">
                          {slot.therapistName}{isSameProvider ? '' : ' (different provider)'}
                        </div>
                        <div className="kiosk-addon-check">{isSelected ? '✓' : ''}</div>
                      </div>
                    );
                  })}
                </div>
                {onBookFull && (
                  <button
                    onClick={() => onBookFull(guest, appointment)}
                    style={{ background: 'none', border: 'none', color: '#7B5EA7', fontSize: 15, fontWeight: 600, marginTop: 12, cursor: 'pointer', textDecoration: 'underline' }}
                  >
                    Or book another day →
                  </button>
                )}
              </div>
            ) : (
              <div style={{ width: '100%', marginTop: 8, textAlign: 'center' }}>
                <div style={{ color: '#999', fontSize: 15, marginBottom: 16 }}>
                  No openings left today
                </div>
                {onBookFull && (
                  <button
                    className="kiosk-nav-forward"
                    style={{ maxWidth: 280, margin: '0 auto' }}
                    onClick={() => onBookFull(guest, appointment)}
                  >
                    Book Another Day
                  </button>
                )}
              </div>
            )}

            <div className="kiosk-bottom-bar">
              {selectedRescheduleSlot !== null ? (
                <>
                  <button className="kiosk-nav-back" onClick={() => setSelectedRescheduleSlot(null)}>Cancel</button>
                  <button
                    className="kiosk-nav-forward"
                    style={{ flex: 1, maxWidth: 280 }}
                    disabled={rescheduleLoading}
                    onClick={() => {
                      const slot = rescheduleSlots[selectedRescheduleSlot];
                      handleRebook(slot.time, slot.therapistId, { cancelFirst: true });
                    }}
                  >
                    {rescheduleLoading ? 'Rescheduling...' : 'Rebook'}
                  </button>
                </>
              ) : (
                <button className="kiosk-nav-back" onClick={onBack}>Leave</button>
              )}
            </div>
          </div>
        )}

        {/* LATE ARRIVAL — warning + check in, with reschedule options if available */}
        {step === 'late_warning' && appointment && (
          <div className="kiosk-screen kiosk-checkin-screen kiosk-fade-in" style={{ textAlign: 'center' }}>
            <div className="kiosk-late-banner">
              <div className="kiosk-late-icon">⏰</div>
              <div className="kiosk-late-label">{Math.round(diffMinutes)} min Late</div>
            </div>
            <div className="kiosk-greeting">Hi {guest?.firstName}!</div>
            {error && (
              <div style={{ background: '#FDEDED', border: '1.5px solid #F5C6CB', borderRadius: 10, padding: '10px 16px', color: '#842029', fontSize: 14, width: '100%', textAlign: 'center', marginBottom: 4 }}>
                {error}
              </div>
            )}
            <div className="kiosk-late-warning-card kiosk-slide-up kiosk-delay-1">
              <div className="kiosk-late-warning-title">
                Your appointment was at {appointment.time}
              </div>
              <div className="kiosk-late-warning-body">
                We may need to adjust your service. {appointment.provider?.name || 'Your provider'} will come talk to you.
              </div>
              <button
                className="kiosk-nav-forward"
                style={{ marginTop: 16, width: '100%', maxWidth: 280 }}
                onClick={() => {
                  // Check in the existing appointment as-is (no cancel/rebook needed)
                  if ((gapMinutes <= 0 || availableAddOns.length === 0) && upsellCategories.length === 0) {
                    confirmCheckIn();
                  } else {
                    setStep('addons');
                  }
                }}
              >
                Check In Anyway
              </button>
            </div>

            {/* Show reschedule options if slots are available */}
            {rescheduleSlots.length > 0 && (
              <div style={{ width: '100%', marginTop: 4 }}>
                <div className="kiosk-late-alert" style={{ marginBottom: 12 }}>
                  Or pick a new time:
                </div>
                <div style={{ maxHeight: 240, overflowY: 'auto' }}>
                  {rescheduleSlots.map((slot, i) => {
                    const t = new Date(slot.time.replace(' ', 'T'));
                    const displayTime = t.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
                    const isSelected = selectedRescheduleSlot === i;
                    const isSameProvider = slot.therapistId === appointment.provider?.id;
                    return (
                      <div
                        key={i}
                        className={`kiosk-late-slot ${isSelected ? 'selected' : ''}`}
                        onClick={() => setSelectedRescheduleSlot(i)}
                      >
                        <div className="kiosk-late-slot-time">{displayTime}</div>
                        <div className="kiosk-late-slot-provider">
                          {slot.therapistName}{isSameProvider ? '' : ' (different provider)'}
                        </div>
                        <div className="kiosk-addon-check">{isSelected ? '✓' : ''}</div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="kiosk-bottom-bar">
              {selectedRescheduleSlot !== null ? (
                <>
                  <button className="kiosk-nav-back" onClick={() => setSelectedRescheduleSlot(null)}>Cancel</button>
                  <button
                    className="kiosk-nav-forward"
                    style={{ flex: 1, maxWidth: 280 }}
                    disabled={rescheduleLoading}
                    onClick={() => {
                      const slot = rescheduleSlots[selectedRescheduleSlot];
                      handleRebook(slot.time, slot.therapistId, { cancelFirst: true });
                    }}
                  >
                    {rescheduleLoading ? 'Rescheduling...' : 'Reschedule'}
                  </button>
                </>
              ) : (
                <button className="kiosk-nav-back" onClick={onBack}>Back to Home</button>
              )}
            </div>
          </div>
        )}

        {/* NO APPOINTMENT — guest found but no booking today, or guest not found */}
        {step === 'no_appointment' && (
          <div className="kiosk-screen kiosk-checkin-screen kiosk-fade-in" style={{ justifyContent: 'center', textAlign: 'center' }}>
            <div style={{ fontSize: 44, marginBottom: 8 }}>
              {notFoundType === 'no_appt' ? '📅' : '🔍'}
            </div>
            <div className="kiosk-screen-title" style={{ fontSize: 24 }}>
              {notFoundType === 'no_appt'
                ? `Hi ${guest?.firstName}!`
                : 'Hmm, we couldn\'t find you'}
            </div>
            <div className="kiosk-screen-sub" style={{ marginTop: 4, maxWidth: 360, marginLeft: 'auto', marginRight: 'auto' }}>
              {notFoundType === 'no_appt'
                ? 'We don\'t see an appointment for you today at this location.'
                : 'We couldn\'t find an account with that phone number.'}
            </div>

            {/* Show upcoming appointments if guest exists */}
            {upcomingAppointments.length > 0 && (
              <div style={{ marginTop: 20, width: '100%', maxWidth: 400, marginLeft: 'auto', marginRight: 'auto' }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#b8860b', marginBottom: 10, textTransform: 'uppercase', letterSpacing: 1 }}>
                  Your upcoming appointments
                </div>
                {upcomingAppointments.slice(0, 3).map((appt, i) => {
                  const isOtherLocation = appt.center?.id !== centerId;
                  const apptDate = new Date(appt.startTime || appt.date);
                  const dateStr = apptDate.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
                  const timeStr = apptDate.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
                  return (
                    <div key={i} style={{
                      background: isOtherLocation ? '#fff3cd' : '#f0f7ff',
                      border: isOtherLocation ? '2px solid #ffc107' : '2px solid #90c0ff',
                      borderRadius: 12,
                      padding: '12px 16px',
                      marginBottom: 8,
                      textAlign: 'left'
                    }}>
                      <div style={{ fontWeight: 600, fontSize: 15 }}>
                        {dateStr} at {timeStr}
                      </div>
                      <div style={{ fontSize: 13, color: '#555', marginTop: 2 }}>
                        {(appt.services?.length > 1
                          ? appt.services.map(s => s.name).join(', ')
                          : appt.service?.name) || 'Service'}
                        {appt.therapist?.name ? ` with ${appt.therapist.name}` : ''}
                      </div>
                      {isOtherLocation ? (
                        <div style={{ fontSize: 13, fontWeight: 700, color: '#856404', marginTop: 4 }}>
                          📍 {appt.center?.name} — different location!
                        </div>
                      ) : (
                        <div style={{ fontSize: 13, fontWeight: 600, color: '#0d6efd', marginTop: 4 }}>
                          📍 This location — but not today
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            <div className="kiosk-bottom-bar">
              <button className="kiosk-nav-back" onClick={() => { setStep('phone'); setError(null); setPhone(''); setNotFoundType(null); setUpcomingAppointments([]); }}>
                ← Try Again
              </button>
              <button
                className="kiosk-nav-forward"
                style={{ flex: 1, maxWidth: 280 }}
                onClick={() => onBook(guest, phone)}
              >
                Book Now
              </button>
            </div>
          </div>
        )}

        {/* ALREADY CHECKED IN */}
        {step === 'already_checked_in' && (
          <div className="kiosk-screen kiosk-success-screen kiosk-fade-in">
            <div className="kiosk-success-icon">✓</div>
            <div className="kiosk-success-title">You're already checked in!</div>
            <div className="kiosk-success-sub">
              Please take a seat and {appointment?.provider?.name || 'your therapist'} will be with you shortly.
            </div>
            <div className="kiosk-bottom-bar">
              <button className="kiosk-nav-back" onClick={onBack}>
                Back to Home
              </button>
            </div>
          </div>
        )}

        {/* LATE ARRIVAL SUCCESS */}
        {step === 'late_success' && rebookedInfo && (
          <div className="kiosk-screen kiosk-success-screen kiosk-fade-in">
            <div className="kiosk-success-icon">✓</div>
            <div className="kiosk-success-title">You're checked in!</div>
            {rebookedInfo.isNow ? (
              <div className="kiosk-success-sub">
                Please take a seat and {rebookedInfo.providerName} will be with you shortly.
              </div>
            ) : (
              <div className="kiosk-late-warning-card" style={{ marginTop: 8 }}>
                <div style={{ fontSize: 16, fontWeight: 600, color: '#E65100', marginBottom: 8 }}>
                  {rebookedInfo.providerName} is in with another guest
                </div>
                <div style={{ fontSize: 22, fontWeight: 700, color: '#333' }}>
                  Your new time is {rebookedInfo.time}
                </div>
                <div style={{ fontSize: 15, color: '#666', marginTop: 8 }}>
                  Please take a seat — {rebookedInfo.providerName} will be with you then.
                </div>
              </div>
            )}
            <div className="kiosk-done-reset">This screen resets automatically</div>
            <div className="kiosk-bottom-bar">
              <button className="kiosk-nav-back" onClick={onBack}>Done</button>
            </div>
          </div>
        )}

        {/* SUCCESS */}
        {step === 'success' && (() => {
          const providerName = appointment?.provider?.name || 'your therapist';
          const minutesUntil = diffMinutes < 0 ? Math.round(Math.abs(diffMinutes)) : 0;
          return (
          <div className="kiosk-screen kiosk-success-screen kiosk-fade-in">
            <div className="kiosk-success-icon">✓</div>
            <div className="kiosk-success-title">
              {visitCount === null
                ? "Welcome!"
                : visitCount === 0
                  ? "Welcome to Cutie Pie!"
                  : `Welcome back${guest?.firstName ? `, ${guest.firstName}` : ''}!`}
            </div>
            {visitCount > 0 && (
              <div className="kiosk-visit-count kiosk-fade-in">
                This is your {visitCount + 1}{visitCount + 1 === 2 ? 'nd' : visitCount + 1 === 3 ? 'rd' : 'th'} visit with us 💕
              </div>
            )}
            <div className="kiosk-success-sub">
              {minutesUntil > 5
                ? `Your appointment is in ${minutesUntil} minutes — feel free to relax. ${providerName} will be with you soon.`
                : `${providerName} will be with you shortly.`}
            </div>
            {(selectedAddOns.length > 0 || bookedUpsells.length > 0) && (
              <div className="kiosk-added-services">
                <div className="kiosk-added-services-title">Added to your visit:</div>
                <div className="kiosk-added-services-list">
                  {[
                    ...selectedAddOns.map(id => availableAddOns.find(a => a.id === id)?.name).filter(Boolean),
                    ...bookedUpsells.map(b => b.name)
                  ].join(' + ')}
                </div>
              </div>
            )}
            {formsSent && (
              <div className="kiosk-forms-notice">
                We texted you a link to complete your forms — please fill them out before your appointment.
              </div>
            )}
            <div className="kiosk-done-reset">This screen resets automatically</div>
            <div className="kiosk-bottom-bar">
              <button className="kiosk-nav-back" onClick={onBack}>Done</button>
            </div>
          </div>
          );
        })()}

      </div>
      {showWarning && <IdleWarningModal countdown={countdown} onDismiss={dismissWarning} onReset={onBack} />}
    </div>
  );
}
